import React, { useEffect, useState } from 'react';
import Navbar from '../../components/Navbar';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API from '../../utils/api';

function BarChart({ data, color = '#6366f1' }) {
  if (!data || data.length === 0) return <p style={{ color: 'var(--text3)', fontSize: 13 }}>No data yet</p>;
  const max = Math.max(...data.map(d => d.value), 1);
  const W = 400, H = 130, padL = 28, padB = 26, padR = 10, padT = 12;
  const chartW = W - padL - padR;
  const chartH = H - padB - padT;
  const barW = Math.max(10, Math.floor(chartW / data.length) - 8);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
      <line x1={padL} y1={padT} x2={padL} y2={padT + chartH} stroke="var(--border)" strokeWidth="1" />
      <line x1={padL} y1={padT + chartH} x2={W - padR} y2={padT + chartH} stroke="var(--border)" strokeWidth="1" />
      {[0.5, 1].map(p => (
        <line key={p} x1={padL} y1={padT + chartH * (1 - p)} x2={W - padR} y2={padT + chartH * (1 - p)}
          stroke="var(--border)" strokeWidth="0.5" strokeDasharray="4 4" />
      ))}
      {data.map((d, i) => {
        const bh = Math.max(4, (d.value / max) * chartH);
        const x = padL + (chartW / data.length) * i + (chartW / data.length - barW) / 2;
        const y = padT + chartH - bh;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={bh} rx={3} fill={color} opacity="0.85" />
            <text x={x + barW / 2} y={y - 3} textAnchor="middle" fill="var(--text2)" fontSize="9">{d.value}</text>
            <text x={x + barW / 2} y={H - 7} textAnchor="middle" fill="var(--text3)" fontSize="9">{d.label.slice(0, 7)}</text>
          </g>
        );
      })}
    </svg>
  );
}

function DonutChart({ segments, size = 120 }) {
  const total = segments.reduce((a, s) => a + s.value, 0);
  const cx = size / 2, cy = size / 2, r = size * 0.38, inner = size * 0.22;
  // If all zero, show equal gray slices as placeholder
  const displaySegs = total === 0
    ? segments.map(s => ({ ...s, value: 1, dimmed: true }))
    : segments;
  const displayTotal = displaySegs.reduce((a, s) => a + s.value, 0);
  let angle = -Math.PI / 2;
  const paths = displaySegs.map(seg => {
    const frac = seg.value / displayTotal;
    const start = angle;
    angle += frac * 2 * Math.PI;
    const end = angle;
    const [x1, y1] = [cx + r * Math.cos(start), cy + r * Math.sin(start)];
    const [x2, y2] = [cx + r * Math.cos(end), cy + r * Math.sin(end)];
    const [xi1, yi1] = [cx + inner * Math.cos(start), cy + inner * Math.sin(start)];
    const [xi2, yi2] = [cx + inner * Math.cos(end), cy + inner * Math.sin(end)];
    const large = frac > 0.5 ? 1 : 0;
    return { d: `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} L ${xi2} ${yi2} A ${inner} ${inner} 0 ${large} 0 ${xi1} ${yi1} Z`, color: seg.dimmed ? 'var(--border)' : seg.color, label: seg.label, value: total === 0 ? 0 : seg.value };
  });
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
      <svg viewBox={`0 0 ${size} ${size}`} style={{ width: size, height: size, flexShrink: 0 }}>
        {paths.map((p, i) => <path key={i} d={p.d} fill={p.color} />)}
        <text x={cx} y={cy + 5} textAnchor="middle" fill="var(--text)" fontSize="14" fontWeight="700">{total}</text>
      </svg>
      <div>
        {paths.map((p, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: p.color, flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: 'var(--text2)' }}>{p.label}: <b style={{ color: 'var(--text)' }}>{p.value}</b></span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function TeacherDashboard() {
  const { user, subscription } = useAuth();
  const [exams, setExams] = useState([]);
  const [banners, setBanners] = useState([]);

  useEffect(() => {
    API.get('/exams').then(r => setExams(r.data));
    API.get('/admin/banners').then(r => setBanners(r.data));
  }, []);

  const now = new Date();
  const subActive = subscription && subscription.status === 'active' && new Date(subscription.end_date) > now;
  const daysLeft = subActive ? Math.ceil((new Date(subscription.end_date) - now) / 86400000) : 0;
  const examsLeft = subActive ? (subscription.exam_limit - subscription.exams_used) : 0;

  const totalAttempts = exams.reduce((a, e) => a + (e.attempt_count || 0), 0);
  const avgAttempts = exams.length > 0 ? (totalAttempts / exams.length).toFixed(1) : 0;
  const published = exams.filter(e => e.is_published).length;
  const draft = exams.length - published;

  const attemptsData = exams.slice(0, 7).map(e => ({ label: e.title, value: e.attempt_count || 0 }));
  const questionsData = exams.slice(0, 7).map(e => ({ label: e.title, value: e.question_count || 0 }));

  return (
    <div>
      <Navbar />
      {banners.map(b => (
        <div key={b.id} style={{ background: 'var(--bg2)', borderBottom: '1px solid var(--border)' }}>
          {b.link_url ? <a href={b.link_url} target="_blank" rel="noreferrer"><img src={b.image_path} alt="banner" style={{ width: '100%', maxHeight: 80, objectFit: 'cover', display: 'block' }} /></a>
            : <img src={b.image_path} alt="banner" style={{ width: '100%', maxHeight: 80, objectFit: 'cover', display: 'block' }} />}
        </div>
      ))}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 20px' }}>
        <div className="page-header flex-between">
          <div>
            <h1>👋 Hello, {user?.name || user?.full_name}!</h1>
            <p>Welcome to your exam dashboard</p>
          </div>
          <Link to="/teacher/exams/new" className="btn btn-primary btn-lg">➕ New Exam</Link>
        </div>

        {/* Subscription card */}
        <div className="card" style={{ marginBottom: 24, borderColor: subActive ? 'var(--success)' : 'var(--danger)', background: subActive ? 'rgba(16,185,129,0.05)' : 'rgba(239,68,68,0.05)' }}>
          <div className="flex-between" style={{ flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16 }}>
                {subActive ? `✅ ${subscription.plan_name}` : '❌ No Active Subscription'}
              </div>
              {subActive && <div style={{ color: 'var(--text2)', fontSize: 13, marginTop: 4 }}>
                {daysLeft} days remaining • {examsLeft} exams remaining • Expires {new Date(subscription.end_date).toLocaleDateString()}
              </div>}
              {!subActive && <div style={{ color: 'var(--text2)', fontSize: 13, marginTop: 4 }}>Subscribe to create and publish exams</div>}
            </div>
            <Link to="/teacher/subscribe" className="btn btn-primary">💳 {subActive ? 'Upgrade / Renew' : 'Subscribe Now'}</Link>
          </div>
        </div>

        {/* Stats */}
        <div className="grid-4" style={{ marginBottom: 28 }}>
          {[
            { label: 'Total Exams', value: exams.length, icon: '📝' },
            { label: 'Published', value: published, icon: '✅' },
            { label: 'Total Attempts', value: totalAttempts, icon: '✍️' },
            { label: 'Avg Attempts', value: avgAttempts, icon: '📈' },
          ].map(s => (
            <div key={s.label} className="card stat-card">
              <div style={{ fontSize: 28, marginBottom: 6 }}>{s.icon}</div>
              <div className="stat-value">{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Charts */}
        {exams.length > 0 && (
          <>
            <div className="grid-3" style={{ marginBottom: 24 }}>
              <div className="card">
                <h3 style={{ fontWeight: 700, marginBottom: 16, fontSize: 15 }}>📊 Exam Status</h3>
                <DonutChart segments={[
                  { label: 'Published', value: published, color: '#10b981' },
                  { label: 'Draft', value: draft, color: '#f59e0b' },
                ]} size={110} />
              </div>
              <div className="card" style={{ gridColumn: 'span 2' }}>
                <h3 style={{ fontWeight: 700, marginBottom: 10, fontSize: 15 }}>✍️ Attempts per Exam</h3>
                <BarChart data={attemptsData} color="#6366f1" />
              </div>
            </div>
            <div className="card" style={{ marginBottom: 28 }}>
              <h3 style={{ fontWeight: 700, marginBottom: 10, fontSize: 15 }}>❓ Questions per Exam</h3>
              <BarChart data={questionsData} color="#10b981" />
            </div>
          </>
        )}

        {/* Recent Exams */}
        <div className="card">
          <div className="flex-between mb-4">
            <h3 style={{ fontWeight: 700 }}>📋 Recent Exams</h3>
            <Link to="/teacher/exams" className="btn btn-secondary btn-sm">View All</Link>
          </div>
          {exams.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text2)' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📝</div>
              <p>No exams yet. Create your first exam!</p>
              <Link to="/teacher/exams/new" className="btn btn-primary" style={{ marginTop: 16 }}>➕ Create Exam</Link>
            </div>
          ) : exams.slice(0, 5).map(e => (
            <div key={e.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--border)', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontWeight: 600 }}>{e.title}</div>
                <div style={{ fontSize: 12, color: 'var(--text2)' }}>{e.subject} • {e.question_count} questions • {e.attempt_count} attempts</div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span className={`badge ${e.is_published ? 'badge-success' : 'badge-warning'}`}>{e.is_published ? 'Published' : 'Draft'}</span>
                <Link to={`/teacher/exams/${e.id}/edit`} className="btn btn-secondary btn-sm">✏️ Edit</Link>
                <Link to={`/teacher/exams/${e.id}/results`} className="btn btn-secondary btn-sm">📊 Results</Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
